import { useEffect, useRef, useState } from "react";
import { Box, Card, CardContent, CircularProgress, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isEqual } from "lodash";
import { searchAssets } from "../../../utils/API/Assets/Assets";
import { searchFacilityRadios } from "../../../utils/API/Radios/Radios";
import { useSelector } from "react-redux";
import CustomFilters from "../../../components/Selects/CustomsSelector/CustomFilters";
import FacilityImage from "../../facilities/FacilityOverview/FacilityImage";
import FacilityImageViewOptions from "../../facilities/FacilityOverview/components/FacilityImageViewOptions";
import MapViewLegend from "./MapViewLegend";
import MaterialUiButton from "../../../components/Buttons/MaterialUiButton/MaterialUiButton";
import NotificationModal from "../../../components/Modals/NotificationModal";
import SimpleSelect from "../../../components/Forms/FieldTypes/Select";
import SimpleTextField from "../../../components/Forms/FieldTypes/TextField";
import MapIcon from "@mui/icons-material/Map";

function MapView(props) {
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, background = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const bgPaper = background?.paper || (isDark ? "#032d42" : "#ffffff");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0");

  const mapCardSx = {
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    height: "74vh",
    minHeight: "320px",
    position: "relative",
    width: "100%",
    borderRadius: "16px",
    border: `1px solid ${borderColor}`,
    backgroundColor: isDark ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.03)",
    overflow: "hidden",
    boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.2)" : "0 4px 20px rgba(0, 0, 0, 0.06)",
  };

  const mapPlaceholderSx = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    padding: 4,
    color: textSecondary,
  };

  const { apiUrl, organizationId, token } = props;

  const { customs, facilities, zones } = useSelector(
    (state) => state.organization,
    isEqual
  );

  const initialState = {
    assets: [],
    filters: {
      customs: [],
      tag: "",
      limit: 1000,
    },
    grid: { x: 0, y: 0 },
    radios: [],
    selectedFacility: { images: [] },
    selectedFacilityId: "",
    selectedFacilityImage: "",
    viewOptions: {
      showZones: true,
      showRadios: true,
    },
    zeroPoint: { disablePlunger: true },
    farPoint: { disablePlunger: true },
  }
  const [state, setState] = useState(initialState);

  const { selectedFacility, selectedFacilityId } = state;

  const initialAlertModalState = {
    alertModalColor: "error",
    modalShow: false,
    modalText: "",
  };
  const [alertModalState, setAlertModalState] = useState(
    initialAlertModalState
  );

  const [img, setImg] = useState({ height: 0, width: 0, isLoaded: false });
  const ref = useRef(null);

  const [loading, setLoading] = useState(false);

  const facilityOptions = Object.keys(facilities)
    .map((k) => facilities[k])
    .map((facility) => ({
      label: facility.name,
      value: facility.facilityId,
    }))
    //sort alphabetically
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleImageLoad = (event) => {
    const height = ref.current.clientHeight;
    const width = event.target.clientWidth;
    setImg({ height, isLoaded: true, width });
  };

  useEffect(() => {
    
    const pngImageUrl = selectedFacility?.images?.find(
      (image) => image.imageType === "PNG"
    )?.url;

    //if no facility is selected, show alert modal
    if(selectedFacilityId && !pngImageUrl) {
    setAlertModalState({
        ...initialAlertModalState,
        modalShow: true,
        modalText: "No facility map image available for this facility",
    });
     //reset state to initial state
      setState(initialState);
      return;
    }
   
    const facilityImage = selectedFacility?.propertiesMap?.facilityImage;
     
      // make sure all 4 are present: facilityId, zeroPoint, farPoint, and grid.
      // if not, show alert modal
      if (selectedFacilityId && (!facilityImage || !facilityImage.zeroPoint || !facilityImage.farPoint || !facilityImage.grid)) {
        setAlertModalState({
        ...initialAlertModalState,
        modalShow: true,
        modalText: "Facility map image is missing grid, zero point, or far point",
      });

      setState(initialState)
      return;
    }

    async function retrieveImage() {

      if (pngImageUrl) {
        const selectedFacilityImage = pngImageUrl;
        const { grid, zeroPoint, farPoint } =
          selectedFacility?.propertiesMap?.facilityImage || {};

        // create a facilityZoneHashMap for easy lookup
        const facilityZoneHashMap = {};
        const zoneIds = [];

        //iterate over zones and add to facilityzonehashmap based on facilityId
        Object.values(zones).forEach((zone) => {
          if (zone.facilityId === selectedFacilityId) {
            facilityZoneHashMap[zone.zoneId] = zone;
            zoneIds.push(zone.zoneId);
          }
        });


        try {
          const response = await searchFacilityRadios(props, zoneIds);

          //create a radioHashMap for easy lookup
          const radioHashMap = response.radios.reduce((acc, radio) => {
            acc[radio.radioId] = radio;
            return acc;
          }, {});

          setState((prevState) => {
            return {
              ...prevState,
              radios: response.radios,
              radioHashMap: radioHashMap,
            };
          });
          setAlertModalState(initialAlertModalState);
        } catch (error) {
          // Handle error and update state
          setAlertModalState({
            ...initialAlertModalState,
            modalShow: true,
            modalText: "Error fetching data",
          });
        } finally {
          // Stop loading
          setLoading(false);
        }

        // Update selected value in state
        setState((prevState) => {
          return {
            ...prevState,
            farPoint,
            grid,
            selectedFacility,
            selectedFacilityImage,
            zones,
            facilityZoneHashMap,
            filters: {
              customs: [],
              tag: "",
              limit: 1000,
            },
            zeroPoint,
          };
        });
      }
    }

    if (selectedFacilityId && pngImageUrl) {
      setLoading(true);
      retrieveImage();
    }
  }, [selectedFacility]);
  function handleFilterSubmit() {
    //if no facility is selected, show alert modal
      if (!state.selectedFacility.facilityId) {
      setAlertModalState({
        ...initialAlertModalState,
        modalShow: true,
        modalText: "Facility must be selected before filters can be set",
      });
      return;
    }
    searchAssets({ apiUrl, isInventory: true, token, organizationId, facilityId: state.selectedFacility.facilityId }, state).then((res) => {
      const { assets } = res;

      const updatedHashMap = { ...state.facilityZoneHashMap };

      //reset the color and opacity of all zones as they were originally
      Object.values(zones).forEach((zone) => {
        if (zone.facilityId === state.selectedFacility.facilityId) {
          updatedHashMap[zone.zoneId] = zone;
        }
      });

      assets.forEach((asset) => {
        const { zone = "" } = asset;
        const zoneObject = state.facilityZoneHashMap[zone.zoneId];
        const { zoneType = "" } = zoneObject || {};

        if (zoneType === "processing") {
          //update the color of the zone to purple and the opacity to 0.5
          updatedHashMap[zone.zoneId] = {
            ...zoneObject,
            color: "purple",
            count: updatedHashMap[zone.zoneId].count ? updatedHashMap[zone.zoneId].count + 1 : 1,
            opacity: 0.5,
          };
        } else if (zoneType === "target") {
          // update the color of the zone to orange and the opacity to 0.5
          updatedHashMap[zone.zoneId] = {
            ...zoneObject,
            count: updatedHashMap[zone.zoneId].count ? updatedHashMap[zone.zoneId].count + 1 : 1,
            color: "#03f8fc",
            opacity: 0.5,
          };
        }
      });

      setState((prevState) => {
        return {
          ...prevState,
          assets: assets,
          facilityZoneHashMap: updatedHashMap,
        };
      });
    });
  }

  const handleChange = (event) => {
    const newValue = event.target.value;
    setState((prevState) => {
      return {
        ...prevState,
        assets: [],
        selectedFacility: facilities[newValue],
        selectedFacilityId: newValue,
      };
    });

    // Call your API with the selected value
    // Example: apiCall(newValue);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
          flexDirection: "column",
          gap: 2,
          color: textSecondary,
        }}
      >
        <CircularProgress sx={{ color: greenColor }} size={40} />
        <Typography variant="body2">Loading map…</Typography>
      </Box>
    );
  }

  return (<>
    <NotificationModal
      confirmationModalShow={alertModalState.modalShow}
      modalClose={() => setAlertModalState(initialAlertModalState)}
      confirmationText={alertModalState.modalText}
      color={alertModalState.alertModalColor}
    />
    <Grid container spacing={2}>
      {/* Legend card */}
      <Grid size={12}>
        <Card
          sx={{
            borderRadius: "14px",
            border: `1px solid ${borderColor}`,
            boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.2)" : "0 2px 12px rgba(0,0,0,0.06)",
            overflow: "hidden",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "3px",
              background: `linear-gradient(90deg, ${navyColor} 0%, ${greenColor} 100%)`,
              opacity: 0.85,
            },
          }}
        >
          <CardContent sx={{ padding: "16px 20px", "&:last-child": { pb: 2 } }}>
            <Typography
              component="h2"
              variant="h6"
              sx={{
                fontWeight: 700,
                color: isDark ? greenColor : navyColor,
                marginBottom: 1,
                fontSize: "1rem",
              }}
            >
              Map Legend
            </Typography>
            <MapViewLegend state setState />
            <Box sx={{ marginTop: 1.5 }}>
              <FacilityImageViewOptions state={state} setState={setState} mainSx={{}} />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Left panel: filters */}
      <Grid size={{ xs: 12, sm: 3 }}>
        <Card
          sx={{
            height: "74vh",
            minHeight: "320px",
            overflow: "auto",
            borderRadius: "14px",
            border: `1px solid ${borderColor}`,
            boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.2)" : "0 2px 12px rgba(0,0,0,0.06)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent sx={{ flex: 1, padding: "20px 16px" }}>
            <SimpleSelect
              getOptionLabel={(option) => option.label}
              label="Select Facility"
              onChange={(e) => handleChange(e)}
              options={facilityOptions}
              placeHolderText="Select facility"
              size="large"
              value={state.selectedFacilityId ?? ""}
              variant="outlined"
              MenuProps={{ disablePortal: false }}
            />
            <Box sx={{ marginTop: 2 }}>
              <SimpleTextField
                onChange={(event) =>
                  setState((prevState) => ({
                    ...prevState,
                    filters: {
                      ...prevState.filters,
                      tag: event.target.value,
                    },
                  }))
                }
                fullWidth
                size="large"
                value={state.filters.tag}
                label="Tag Id"
              />
            </Box>
            <Box sx={{ marginTop: 2 }}>
              <CustomFilters
                customs={customs}
                state={state}
                setState={setState}
                xsSize={12}
                cardSx={{}}
              />
            </Box>
          </CardContent>
          <Box sx={{ padding: "12px 16px", borderTop: `1px solid ${borderColor}` }}>
            <MaterialUiButton
              label="Display"
              data-testid="Display Button"
              onClick={() => handleFilterSubmit()}
              fullWidth
            />
          </Box>
        </Card>
      </Grid>

      {/* Right panel: map area */}
      <Grid size={{ xs: 12, sm: 9 }}>
        <Card raised sx={mapCardSx} ref={ref}>
          {state.selectedFacility && state.selectedFacilityImage ? (
            <FacilityImage
              facilityImage={state.selectedFacilityImage}
              handleImageLoad={handleImageLoad}
              img={img}
              state={state}
            />
          ) : (
            <Box sx={mapPlaceholderSx}>
              <MapIcon sx={{ fontSize: 64, opacity: 0.4 }} />
              <Typography variant="body1" sx={{ fontWeight: 500, textAlign: "center" }}>
                Select a facility to view the map
              </Typography>
              <Typography variant="body2" sx={{ textAlign: "center", maxWidth: 320 }}>
                Choose a facility from the panel and click Display to load assets and zones.
              </Typography>
            </Box>
          )}
        </Card>
      </Grid>
    </Grid>
  </>);
}

export default MapView;
